import { PropertyCardProps } from "@/cms/models";
import {
  getCaseClassification,
  getCaseUrl,
  getLabel,
  isRental,
} from "@/utils/helper";
import Link from "next/link";
import { useState } from "react";
import { SpinnerSvg } from "./icons/Spinner";
import Card from "./ui/Card";
import DetailsWrapper from "./ui/propertyfacts/DetailsWrapper";

const PropertyRowCard: React.FC<
  PropertyCardProps & { shadow: "none" | "little" | "lots" }
> = ({ property, shadow }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const imageUrl = property.images.find((x) => x.lgUrl)?.lgUrl;

  const classifications = getCaseClassification(property);
  const isRent = isRental(classifications);
  const isCommercial = property.isCommercial;

  const propertyfacts = [
    !isRent
      ? {
          type: "StringDetails",
          data: {
            label: "Kontantpris",
            value: property.priceInfo,
          },
          hasValue: !!property.priceInfo,
        }
      : null,
    {
      type: "StringDetails",
      data: {
        label: "Leje pr. md.",
        value: property.priceInfo,
      },
      hasValue: property.isActive && !!property.rentMonthly,
    },
    {
      type: "StringDetails",
      data: {
        label: "Leje pr. år",
        value: property.priceInfo,
      },
      hasValue: property.isActive && !!property.rentAnnually,
    },
    {
      type: "StringDetails",
      data: {
        label: "Afkast i %",
        value: new Intl.NumberFormat("da-DK", {
          style: "percent",
          minimumFractionDigits: 1,
        }).format(property.rateOfReturn / 100),
      },
      hasValue: !!property.rateOfReturn,
    },
    !isRent
      ? {
          type: "StringDetails",
          data: {
            label: "Boligareal",
            value: `${new Intl.NumberFormat("da-DK", {
              unit: "meter",
            }).format(property.housingArea)} m²`,
          },
          hasValue: !!property.housingArea,
        }
      : null,
    !isRent
      ? {
          type: "StringDetails",
          data: {
            label: "Erhvervsareal",
            value: `${new Intl.NumberFormat("da-DK", {
              unit: "meter",
            }).format(property.commercialArea)} m²`,
          },
          hasValue: !!property.commercialArea,
        }
      : null,
    isCommercial
      ? {
          type: "StringDetails",
          data: {
            label: "Etageareal",
            value: `${new Intl.NumberFormat("da-DK", {
              unit: "meter",
            }).format(property.floorArea)} m²`,
          },
          hasValue: !!property.floorArea,
        }
      : null,
    !isRent
      ? {
          type: "StringDetails",
          data: {
            label: "Grundareal",
            value:
              property.plotArea === 0
                ? "- m²"
                : `${new Intl.NumberFormat("da-DK", {
                    unit: "meter",
                  }).format(property.plotArea)} m²`,
          },
          hasValue: true,
        }
      : null,
    {
      type: "StringDetails",
      data: {
        label: "Værelser",
        value: property.numberOfRooms,
      },
      hasValue: !!property.numberOfRooms,
    },
    {
      type: "BuiltYearDetails",
      data: {
        label: "Byggeår",
        yearBuilt: property.yearBuilt,
        yearRenovationAddition: property.yearRenovationAddition,
      },
      hasValue: !!property.yearBuilt,
    },
    {
      type: "StringDetails",
      data: {
        label: "Ejendomstype",
        value: property.estateType,
      },
      hasValue: !!property.estateType,
    },
    {
      type: "EnergyLabelDetails",
      data: {
        label: "Energimærke",
        energyLabel: property.energyLabel,
      },
      hasValue: !!property.energyLabel,
    },
  ]
    .filter((x) => !!x)
    .filter((x) => x.hasValue)
    .slice(0, 8);
  const label = getLabel(property);
  return (
    <Link
      aria-label={property.advertisementAddressOrDisplayName}
      onClick={() => setIsLoading(true)}
      href={getCaseUrl(property)}
      className="flex relative h-full lg:h-[500px] justify-end"
      data-component-type="PropertyRowCard"
    >
      <Card className="flex flex-col lg:flex-row w-full" shadow={shadow}>
        {isLoading && (
          <div className="absolute flex justify-center items-center inset-0 bg-gray-800 opacity-50 transition-opacity z-20">
            <SpinnerSvg />
          </div>
        )}

        <div className="flex h-[300px] lg:h-full lg:w-[60%] relative">
          {label && (
            <div className="absolute z-10 max-w-full homepage-card-label-rounded-bl truncate homepage-text-sm top-0 right-0 uppercase bg-[var(--label-bg-color)] text-[var(--label-text-color)] px-4 py-2 text-xs">
              <span>{label}</span>
            </div>
          )}
          {imageUrl && (
            <img
              src={imageUrl}
              alt={property.advertisementAddressOrDisplayName}
              className=" object-cover h-full w-full"
            />
          )}
        </div>
        <div className="col-span-1 lg:w-[40%] flex flex-col gap-2 p-4 lg:p-10">
          {property.marketingTexts?.headline && (
            <p className="homepage-text-lg line-clamp-2">
              {property.marketingTexts.headline}
            </p>
          )}
          <div className="homepage-text-md">
            {property.advertisementAddressOrDisplayName}
          </div>
          <div className="homepage-text-sm flex flex-col pt-10 gap-1">
            {propertyfacts.map((fact, index) => (
              <DetailsWrapper key={`propertyfacts-${index}`} {...fact} />
            ))}
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default PropertyRowCard;
